import React, { useState } from 'react';
import Input from 'components/atoms/Input';
import Button from 'components/atoms/Button';
import Link from 'components/atoms/Link';
import ErrorMessage from 'components/molecules/ErrorMessage';
import LoadingSpinner from 'components/atoms/LoadingSpinner';
import { ResetPasswordFormProps } from 'types/organisms.types';
import { ResetPasswordFormData } from 'types/auth.types';

interface FormErrors {
  newPassword?: string;
  confirmPassword?: string;
}

const ResetPasswordForm: React.FC<ResetPasswordFormProps> = ({
  onSubmit,
  onBackToLogin,
  loading = false,
  error = '',
  success = '',
  tokenValid = false,
  tokenLoading = true,
}) => {
  const [formData, setFormData] = useState<ResetPasswordFormData>({
    newPassword: '',
    confirmPassword: '',
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [showError, setShowError] = useState<boolean>(true);
  const [showSuccess, setShowSuccess] = useState<boolean>(true);
  const [pwVisible, setPwVisible] = useState<boolean>(false);
  const [confirmPwVisible, setConfirmPwVisible] = useState<boolean>(false);

  React.useEffect(() => {
    setShowError(!!error);
  }, [error]);

  React.useEffect(() => {
    setShowSuccess(!!success);
  }, [success]);

  const validate = (): boolean => {
    const newErrors: FormErrors = {};
    if (!formData.newPassword) {
      newErrors.newPassword = 'Password is required';
    } else if (formData.newPassword.length < 8) {
      newErrors.newPassword = 'Password must be at least 8 characters';
    } else if (!/[A-Z]/.test(formData.newPassword)) {
      newErrors.newPassword = 'Password must contain at least one uppercase letter';
    } else if (!/[a-z]/.test(formData.newPassword)) {
      newErrors.newPassword = 'Password must contain at least one lowercase letter';
    } else if (!/[0-9]/.test(formData.newPassword)) {
      newErrors.newPassword = 'Password must contain at least one number';
    } else if (!/[^A-Za-z0-9]/.test(formData.newPassword)) {
      newErrors.newPassword = 'Password must contain at least one special character';
    }
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.newPassword !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (field: keyof ResetPasswordFormData, value: string): void => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    if (loading) return;
    setShowError(true);
    if (validate()) {
      await onSubmit(formData);
    }
  };

  const handleBackToLogin = (e: React.MouseEvent): void => {
    e.preventDefault();
    onBackToLogin();
  };

  const isFormDisabled = loading;

  if (tokenLoading) {
    return (
      <div className="w-full max-w-md flex flex-col items-center space-y-4">
        <LoadingSpinner />
        <p className="text-sm text-gray-600">Verifying reset link...</p>
      </div>
    );
  }

  if (!tokenValid) {
    return (
      <div className="w-full max-w-md space-y-6 text-center">
        <h2 className="text-xl font-semibold text-gray-900">Invalid or Expired Link</h2>
        <p className="text-sm text-gray-600">
          This password reset link is invalid or has expired. Please request a new one.
        </p>
        <Link
          onClick={handleBackToLogin}
          className="text-sm font-medium text-teal-600 hover:text-teal-700 transition-colors duration-200"
        >
          ← Back to Sign In
        </Link>
      </div>
    );
  }

  return (
    <div className="w-full max-w-md space-y-6">
      <div className="text-center">
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Set New Password</h2>
        <p className="text-sm text-gray-600">Enter and confirm your new password below.</p>
      </div>

      {success && showSuccess && (
        <ErrorMessage
          error={success}
          variant="info"
          onDismiss={() => setShowSuccess(false)}
          className="mb-4"
        />
      )}

      {error && showError && (
        <ErrorMessage
          error={error}
          onDismiss={() => setShowError(false)}
          className="mb-4"
        />
      )}

      <form onSubmit={handleSubmit} className="space-y-4" noValidate>
        <div className="relative">
          <Input
            type={pwVisible ? 'text' : 'password'}
            placeholder="New Password"
            value={formData.newPassword}
            onChange={(e) => handleChange('newPassword', e.target.value)}
            error={errors.newPassword}
            disabled={isFormDisabled}
            required
            autoComplete="new-password"
          />
          <button
            type="button"
            className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-gray-500 hover:text-gray-700"
            onClick={() => setPwVisible((v) => !v)}
            disabled={isFormDisabled}
            aria-label={pwVisible ? 'Hide password' : 'Show password'}
          >
            {pwVisible ? 'Hide' : 'Show'}
          </button>
        </div>

        <div className="relative">
          <Input
            type={confirmPwVisible ? 'text' : 'password'}
            placeholder="Confirm New Password"
            value={formData.confirmPassword}
            onChange={(e) => handleChange('confirmPassword', e.target.value)}
            error={errors.confirmPassword}
            disabled={isFormDisabled}
            required
            autoComplete="new-password"
          />
          <button
            type="button"
            className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-gray-500 hover:text-gray-700"
            onClick={() => setConfirmPwVisible((v) => !v)}
            disabled={isFormDisabled}
            aria-label={confirmPwVisible ? 'Hide password' : 'Show password'}
          >
            {confirmPwVisible ? 'Hide' : 'Show'}
          </button>
        </div>

        <Button
          type="submit"
          variant="teal"
          className="w-full py-3 font-semibold"
          loading={isFormDisabled}
          disabled={isFormDisabled}
        >
          {isFormDisabled ? 'Updating...' : 'Update Password'}
        </Button>
      </form>

      <div className="text-center">
        <Link
          onClick={handleBackToLogin}
          className="text-sm font-medium text-teal-600 hover:text-teal-700 transition-colors duration-200"
          disabled={isFormDisabled}
        >
          ← Back to Sign In
        </Link>
      </div>
    </div>
  );
};

export default ResetPasswordForm;
